import { useMemo, useState } from 'react';
import { api, ApiError } from '../api';
import { browserZone, zoneAbbreviation } from '../format';
import type { LocationSummary, TimeZoneSettings } from '../types';

/** Every zone the browser knows, with the common US ones first. */
function zoneList(): string[] {
  const common = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Phoenix',
    'America/Los_Angeles',
    'America/Anchorage',
    'Pacific/Honolulu',
  ];
  let all: string[] = [];
  try {
    all = (Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.('timeZone') ?? [];
  } catch {
    all = [];
  }
  return [...common, ...all.filter((zone) => !common.includes(zone))];
}

function label(zone: string): string {
  return `${zone.replace(/_/g, ' ')} (${zoneAbbreviation(zone)})`;
}

/** Settings → General → Time zone. */
export function TimeZoneCard({
  settings,
  locations,
  onSaved,
}: {
  settings: TimeZoneSettings;
  locations: LocationSummary[];
  onSaved: (next: TimeZoneSettings) => void;
}) {
  const [zone, setZone] = useState(settings.defaultZone);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<{ ok: boolean; text: string } | null>(null);
  const zones = useMemo(zoneList, []);

  async function save(patch: Partial<TimeZoneSettings>, text: string) {
    setBusy(true);
    setNote(null);
    try {
      const next = await api.saveTimeZones(patch);
      onSaved(next);
      setZone(next.defaultZone);
      setNote({ ok: true, text });
    } catch (cause) {
      setNote({ ok: false, text: cause instanceof ApiError ? cause.message : String(cause) });
    } finally {
      setBusy(false);
    }
  }

  const missing = locations.filter((location) => location.trackingEnabled && !location.timezone);
  const byZone = new Map<string, number>();
  for (const location of locations) {
    if (location.timezone) byZone.set(location.timezone, (byZone.get(location.timezone) ?? 0) + 1);
  }

  return (
    <div className="card">
      <div className="card-head">
        <h2>Time zone</h2>
      </div>
      <p className="card-sub">
        Decides which calendar day a call, appointment or deal falls on in every report, the times shown in tables,
        and what “today” means to the Assistant.
      </p>
      <div className="stack">
        <div className="field">
          <label htmlFor="default-zone">Agency time zone</label>
          <div className="row nowrap-row">
            <select id="default-zone" value={zone} onChange={(event) => setZone(event.target.value)} style={{ maxWidth: 420 }}>
              <option value="">Each person’s computer ({label(browserZone())} here)</option>
              {zones.map((item) => (
                <option key={item} value={item}>
                  {label(item)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn btn-sm btn-primary"
              disabled={busy || zone === settings.defaultZone}
              onClick={() => save({ defaultZone: zone }, 'Time zone saved.')}
            >
              Save
            </button>
          </div>
          <span className="small muted">
            Used for all sub-accounts together, and for any sub-account when the switch below is off.
          </span>
        </div>

        <div className="field">
          <label className="switch">
            <input
              type="checkbox"
              role="switch"
              checked={settings.followSubaccount}
              disabled={busy}
              onChange={(event) =>
                save(
                  { followSubaccount: event.target.checked },
                  event.target.checked
                    ? 'A single sub-account now shows in its own time zone.'
                    : 'Everything now shows in the agency time zone.',
                )
              }
            />
            When one sub-account is selected, use that sub-account’s own time zone
          </label>
          <span className="small muted">
            Each sub-account’s zone comes from its HighLevel settings
            {byZone.size
              ? ` — ${[...byZone.entries()]
                  .sort((a, b) => b[1] - a[1])
                  .slice(0, 4)
                  .map(([name, count]) => `${count} in ${name.replace(/_/g, ' ')}`)
                  .join(', ')}${byZone.size > 4 ? ', …' : ''}`
              : ''}
            .{' '}
            {missing.length
              ? `${missing.length} tracked sub-account${missing.length === 1 ? ' has' : 's have'} none set and use the agency time zone.`
              : ''}
          </span>
        </div>
        {note ? <div className={`inline-result${note.ok ? '' : ' bad'}`}>{note.text}</div> : null}
      </div>
    </div>
  );
}
